// 取得済み時系列に対する決定論的な分析。異常検知のしきい値・計算は
// バッチと同じ scripts/lib/anomaly.js を共有し、監視ダッシュボードと一貫させる。
import { analyzeSeries, classify } from "../../scripts/lib/anomaly.js";

export const SUPPORTED_OPERATIONS = ["summary", "trend", "anomaly"];

const NON_NUMERIC = new Set(["date", "portid", "portname", "ObjectId"]);

/** レコード群から数値指標の列名を推定する。 */
export function numericColumns(records) {
  const cols = new Set();
  for (const record of records.slice(0, 50)) {
    for (const [key, value] of Object.entries(record)) {
      if (!NON_NUMERIC.has(key) && typeof value === "number") cols.add(key);
    }
  }
  return [...cols];
}

function round(value, digits = 2) {
  if (!Number.isFinite(value)) return null;
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}

function basicStats(values) {
  const n = values.length;
  if (n === 0) return { count: 0 };
  const sum = values.reduce((s, v) => s + v, 0);
  const mean = sum / n;
  const variance =
    n > 1 ? values.reduce((s, v) => s + (v - mean) ** 2, 0) / n : 0;
  return {
    count: n,
    latest: values[n - 1],
    sum: round(sum),
    mean: round(mean),
    min: Math.min(...values),
    max: Math.max(...values),
    std: round(Math.sqrt(variance)),
  };
}

function valuesFor(records, metric) {
  return records
    .map((r) => r[metric])
    .filter((v) => v != null && Number.isFinite(v));
}

/**
 * 決定論的な分析を実行する。
 * @param {object[]} records 時系列レコード（date + 指標列）
 * @param {string} operation summary | trend | anomaly
 * @param {string} [metric] 対象指標（省略時は全数値列）
 */
export function runMetricAnalysis({ records, operation, metric }) {
  if (!Array.isArray(records) || records.length === 0) {
    throw new Error("分析対象のレコードがありません。");
  }
  if (!SUPPORTED_OPERATIONS.includes(operation)) {
    throw new Error(
      `operation は ${SUPPORTED_OPERATIONS.join(" / ")} のいずれかです。`,
    );
  }

  const metrics = metric ? [metric] : numericColumns(records);
  if (metrics.length === 0) {
    throw new Error("数値指標の列が見つかりません。");
  }

  if (operation === "summary") {
    const results = metrics.map((m) => ({
      metric: m,
      ...basicStats(valuesFor(records, m)),
    }));
    return { operation, recordCount: records.length, metrics: results };
  }

  // trend / anomaly: 28日平滑＋年間Zスコアで現在水準・前月比・σ・シグナルを出す。
  const results = metrics.map((m) => {
    const series = records.map((r) => ({ date: r.date, value: r[m] }));
    const stat = analyzeSeries(series);
    if (!stat) {
      return { metric: m, signal: "ok", insufficient: true };
    }
    return {
      metric: m,
      signal: classify(stat),
      latest: stat.latest,
      latestDate: stat.latestDate,
      current: stat.current,
      previousMonth: stat.baseline,
      pctChange: stat.pctChange,
      sigma: stat.sigma,
      yearMean: stat.mean,
      points: stat.points,
    };
  });

  return { operation, recordCount: records.length, metrics: results };
}
